import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Learning Trajectories",
  "lead": "While most children now spend many years in school, they often emerge without even the most basic reading and math skills. Learning trajectories show how much children learn as they progress through school. They can help us understand the process that leads to low learning outcomes, when and how to intervene during that process, and what the simulated impact of different policies might be on the urgent challenge of low learning."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    h3: "h3",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Explorer, Figure, HighlightByIndex, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollyStep, ScrollySticky, SimulationsTab, TrajectoriesTab} = _components;
  if (!Explorer) _missingMdxReference("Explorer", true);
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollyStep) _missingMdxReference("ScrollyStep", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  if (!SimulationsTab) _missingMdxReference("SimulationsTab", true);
  if (!TrajectoriesTab) _missingMdxReference("TrajectoriesTab", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_LEARNING"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Learning trajectories show how many years or grades it takes for children to acquire foundational skills. Because these trajectories track the pace of learning in the system, they can help us understand how many children struggle to acquire these skills, when and how to intervene, and how different policies might impact the urgent challenge of low learning."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "what-can-learning-trajectories-tell-us",
      children: "What can learning trajectories tell us?"
    }), "\n", _jsx(_components.p, {
      children: "Ideally, learning trajectories are steep. However, too often we see learning trajectories that are flat, indicating a slow pace of learning. A slow start can have long-term consequences. This is because when children fall behind, the curriculum often moves on to more advanced material with which they lack the prerequisite skills to engage."
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "The trajectories of many countries reveal that the pace of learning is far too slow, and that this is true from the very beginning of school."
      }), " In Grades 1 and 2, when global goals and many national curricula assume that children are learning to read, only a small minority of children actually do so."]
    }), "\n", _jsx(IndicatorReference, {
      id: "4.1.1",
      label: "4.1.1a",
      children: _jsxs(_components.p, {
        children: ["These skills, which are measured in the sixth round of the UNICEF Multiple Indicator Cluster Surveys (MICS6), are the basic prerequisites for future learning and are measured here by whether children could read a simple 70-word story aloud (i.e. “Manh is in class two.”) and answer five simple questions about it (i.e. “What class is Manh in?”). These foundational skills are typically below the minimum proficiency level defined by ", _jsx(_components.a, {
          href: "https://sdgs.un.org/goals/goal4",
          children: "Sustainable Development Goal (SDG) global indicator 4.1.1 a"
        }), ". The calculated trajectories therefore overestimate progress towards SDG 4.1.1 a, but are still useful estimates to illustrate the pace of learning."]
      })
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_CURRENT"
        })
      }), _jsx(ScrollySection, {
        id: "HIGHLIGHT_VIETNAM",
        children: _jsxs(_components.p, {
          children: [_jsx(HighlightByIndex, {
            index: "2",
            children: "Viet Nam"
          }), " is a good example of\na relatively high-performing country trajectory. During the initial grades\nof school, most children in Viet Nam learn foundational reading skills."]
        })
      }), _jsx(ScrollySection, {
        id: "LMIC_GRADE_3",
        children: _jsxs(_components.p, {
          children: ["The learning trajectory in many other", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "low and lower-middle income"
          }), " ", "\ncountries is much flatter. Only one in four children in the average country\ncurrently learns foundational reading skills by Grade 3."]
        })
      }), _jsx(ScrollySection, {
        id: "LMIC_GRADE_6",
        children: _jsxs(_components.p, {
          children: ["Low learning persists as children continue through school. Just over half of\nGrade 6 students in the average", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "low- and lower-middle-income"
          }), " ", "\ncountry in the data set have gained what are typically Grade 2-3 reading\nskills."]
        })
      }), _jsx(ScrollySection, {
        id: "DIFFERENCE_TRAJECTORIES",
        children: _jsx(_components.p, {
          children: "However, the average trajectory hides variation among countries. Anywhere\nbetween 3 per cent and 76 per cent of children in low- and\nlower-middle-income countries in the data set can read by Grade 3. This\nshows that countries that commit to improving foundational learning can do\nso, even at relatively low levels of national income."
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.p, {
      children: "For a country that wants to address low learning, the policy takeaways are:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "Measure and prioritize foundational skills, beginning in the early grades."
      }), "\n", _jsx(_components.li, {
        children: "Align instruction to children’s actual pace of learning. This can be done in many ways, including by setting clear learning goals focused on foundational learning, adjusting the pace of the curriculum and supporting teachers to understand and adapt their teaching to children’s current learning levels."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      id: "policy-simulations-access-and-learning",
      children: "Policy simulations: access and learning"
    }), "\n", _jsx(_components.p, {
      children: "We can use learning trajectories to compare the potential impact of different policies. Here, we simulate the impact of policies aimed at increasing access and enhancing learning. Access-oriented policies seek to increase the number of years children spend in school. They include policies such as free primary and secondary education, school building, automatic promotion and other policies that primarily focus on enrolment or attainment. Learning-oriented policies seek to increase how much children learn at each grade in school. Examples of these policies include setting and measuring progress against clear learning goals, realigning instruction to match children’s pace of learning, and supporting effective teaching."
    }), "\n", _jsxs(_components.p, {
      children: ["Every child should have access to school, and getting children into school is a necessary first step towards universal learning. However, these simulations show that further ", _jsx(HighlightByIndex, {
        index: "2",
        children: "increases in access"
      }), " will, on their own, do very little to address learning. In contrast, there are large potential gains to be made by ", _jsx(HighlightByIndex, {
        index: "3",
        children: "increasing learning per grade"
      }), " to match the pace of higher-performing low- and lower-middle-income countries."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_SIMULATION_LMIC"
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON",
        children: _jsx(_components.p, {
          children: "The difference in performance between lower- and higher-performing countries\nis driven by both providing greater access to school and, in particular,\nenabling children to learn more per grade."
        })
      }), _jsx(ScrollySection, {
        id: "FADED",
        children: _jsx(_components.p, {
          children: "These simulations show trajectories by age. They include both children in\nand out of school. Since out-of-school children tend to have lower learning\nachievement, these learning trajectories are flatter than those by grade."
        })
      }), _jsx(ScrollySection, {
        id: "CURRENT",
        children: _jsxs(_components.p, {
          children: ["Averaging across five low-performing", " ", "\n", _jsx(HighlightByIndex, {
            index: "4",
            children: "low- and lower-middle-income"
          }), " ", "\ncountries, only 8 per cent of 9-year-olds can read at an age-appropriate\nlevel."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_ACCESS",
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(HighlightByIndex, {
            index: "2",
            children: "access simulation"
          }), " ", "\napproximates what might happen if all children were in school. It does this\nby estimating what would happen if all the children who never attended\nschool, or who dropped out, instead attained the same average grade and\nlearning level as their in-school peers of the same age. In the same five\nlow-performing countries, the simulations suggest that this would increase\nthe number of 9-year-olds who can read by 1 percentage point."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_LEARNING",
        children: _jsxs(_components.p, {
          children: ["If 9-year-olds in these countries continued to reach the same grade as they\ndo now, but instead were", " ", "\n", _jsx(HighlightByIndex, {
            index: "3",
            children: "learning as much per grade"
          }), "as\nchildren in the five highest-performing low- and lower-middle-income\ncountries, then 43 per cent of them might be able to read – an increase of\n35 percentage points."]
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "policy-simulations-equality",
      children: "Policy simulations: equality"
    }), "\n", _jsx(_components.p, {
      children: "Learning trajectories are also a powerful tool to understand the differences in learning between groups inside countries. Here, we compare the learning of the rich and the poor, and simulate the impact of policies that try to reduce inequality between them."
    }), "\n", _jsxs(_components.p, {
      children: ["Averaging across all low- and lower-middle-income countries in the data set, the children of the rich (family wealth in the top 20 per cent) go to school more, and learn more in each grade, than the children of the poor (family wealth in the bottom 20 per cent). The ", _jsx(HighlightByIndex, {
        index: "2",
        children: "equal access simulation"
      }), " shows what might happen if instead the poor got as much schooling as the rich, while the ", _jsx(HighlightByIndex, {
        index: "3",
        children: "equal learning simulation"
      }), " shows what might happen if instead the poor learned as much per grade as the rich. In both scenarios, and especially in the equal learning simulation, inequality would decrease."]
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "However, even the children of the rich are not getting a very good education"
      }), ". Nearly half of the children of the rich are failing to acquire foundational reading skills, putting a limit on the potential impact of equity-oriented policies. Simulations of equality between boys and girls, or urban and rural children, yield even smaller gains."]
    }), "\n", _jsxs(_components.p, {
      children: ["For a country that wants to address low learning, the policy takeaway is that closing gaps between groups inside countries is only a part of the solution. ", _jsx(_components.strong, {
        children: "An equally pressing challenge is to make systemic changes that improve learning for all children – rich and poor, boys and girls, in urban and rural areas – to achieve a transition from a low- to a high-performing system."
      })]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TRAJECTORIES_SIMULATION_EQUALITY"
        })
      }), _jsx(ScrollySection, {
        id: "CURRENT",
        children: _jsxs(_components.p, {
          children: ["The children of the ", _jsx(HighlightByIndex, {
            index: "4",
            children: "poor"
          }), " are\non a lower learning trajectory than the children of the rich. Averaging\nacross 23 low- and lower-middle-income countries, only 19 per cent of\nchildren from poor families have foundational reading skills by age 9 years."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_ACCESS",
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(HighlightByIndex, {
            index: "2",
            children: "first simulation"
          }), " shows\nwhat might happen if children from poor families got as much schooling as\nchildren from rich families (in terms of grade attainment by age). Averaging\nacross 23 countries, this would only raise foundational reading skills by 4\npercentage points at age 9 years."]
        })
      }), _jsx(ScrollySection, {
        id: "SIMULATION_LEARNING",
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(HighlightByIndex, {
            index: "3",
            children: "second simulation"
          }), " shows\nwhat might happen if children from poor families maintained their current\nlevel of schooling (i.e. attained the same grade as they do now) but learned\nas much per grade as children from rich families. This would potentially\nincrease their foundational reading skills by 17 percentage points at age 9\nyears."]
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON",
        children: _jsx(_components.p, {
          children: "If the children of the poor got as much schooling and learned as much as the\nchildren of the rich (i.e. if the poor had the same trajectory as the rich)\nthe foundational reading skills of children of the poor might go up by 29\npercentage points at age 9 years."
        })
      }), _jsx(ScrollySection, {
        id: "COMPARISON_GROUP",
        children: _jsx(_components.p, {
          children: "However, this would still leave nearly half of children without foundational\nreading skills. Even the children of rich families are not getting a good\neducation in many countries."
        })
      }), _jsx(ScrollyStep, {
        id: "default"
      })]
    }), "\n", _jsx(_components.h2, {
      id: "create-your-own-learning-trajectories-and-policy-simulations",
      children: "Create your own learning trajectories and policy simulations"
    }), "\n", _jsx(_components.p, {
      children: "Below is a tool to create your own learning trajectories and run policy simulations. You can explore data from multiple countries for both foundational reading and mathematics skills."
    }), "\n", _jsxs(_components.p, {
      children: ["As a reminder, reading skills are measured by whether children could read a simple 70-word story aloud and answer five simple questions about the story. Mathematics skills are measured over four domains (number identification, number discrimination, simple addition, and number patterns) using 21 simple questions. A child is considered to have mastered these basic reading and mathematics skills if they answered all questions correctly. These are typically Grade 2 or 3 level skills and are below the minimum proficiency level defined by ", _jsx(_components.a, {
        href: "https://sdgs.un.org/goals/goal4",
        children: "SDG global indicator 4.1.1a"
      }), "."]
    }), "\n", _jsxs(Explorer, {
      children: [_jsx(TrajectoriesTab, {
        children: _jsx(_components.p, {
          children: "Learning trajectories vary greatly across contexts. This tool allows you to\nbuild, compare and export the trajectories that are most relevant for you.\nYou can visualize and compare the trajectories of different countries, or\nyou can build and compare the trajectories of groups inside a country."
        })
      }), _jsx(SimulationsTab, {
        children: _jsx(_components.p, {
          children: "These tools allow you to run policy simulations and analyse results to\ninform policy priorities. You can run policy simulations at the country\nlevel, where you can estimate the impact of achieving universal schooling\nand of achieving the learning per grade of a comparison country. You can\nalso run policy simulations for groups inside a country, where you can\nsimulate what the impact would be if two population groups had the same\nrates of school attainment and learning."
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "Learning trajectories reveal how quickly children acquire foundational learning skills. They are an important tool that can be used to raise public awareness of low learning outcomes and help policymakers plan how to take action."
    }), "\n", _jsx(_components.h3, {
      id: "acknowledgements",
      children: "Acknowledgements"
    }), "\n", _jsxs(_components.p, {
      children: ["The content of this webpage was created by the ", _jsx(_components.a, {
        href: "http://riseprogramme.org/",
        children: "RISE (Research on Improving Systems of Education) Programme"
      }), " – the large-scale education systems research programme supported by funding from the ", _jsx(_components.a, {
        href: "https://www.gov.uk/government/organisations/foreign-commonwealth-development-office",
        children: "United Kingdom’s Foreign, Commonwealth and Development Office (FCDO)"
      }), ", the ", _jsx(_components.a, {
        href: "http://dfat.gov.au",
        children: "Australian Government’s Department of Foreign Affairs and Trade (DFAT)"
      }), ", and the ", _jsx(_components.a, {
        href: "https://www.gatesfoundation.org",
        children: "Bill and Melinda Gates Foundation"
      }), ". The Programme is managed and implemented through a partnership between ", _jsx(_components.a, {
        href: "https://www.opml.co.uk",
        children: "Oxford Policy Management"
      }), " and the ", _jsx(_components.a, {
        href: "http://bsg.ox.ac.uk",
        children: "Blavatnik School of Government at the University of Oxford"
      }), "."]
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://riseprogramme.org/tools/learning-trajectories",
            children: "Learning trajectories resources – RISE"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://data.unicef.org/resources/are-children-really-learning-foundational-skills-report/",
            children: "Are Children Really Learning? Exploring foundational skills in the midst of a learning crisis – UNICEF 2022"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.sciencedirect.com/journal/international-journal-of-educational-development/special-issue/1035CNWP9N3",
            children: "Special issue on learning profiles (open source) – International Journal of Educational Development (IJED)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://mics.unicef.org/files?job=W1siZiIsIjIwMjAvMDUvMTIvMTgvMjUvNDUvNzAxLzIwXzA1XzA4X01JQ1NfR3VpZGVib29rX2NvbXByZXNzZWQucGRmIl1d&sha=6d386818d588d05c",
            children: "How to analyze MICS6 education data - UNICEF"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://riseprogramme.org/publications/descriptive-learning-trajectories-and-policy-simulations-using-mics6-data",
            children: "Simulations methodology – RISE"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
